import { z } from 'zod';
export const ResponsePromptTypeSchema = z.enum(['folder', 'file']);
export const ResponsePromptVersionSourceSchema = z.enum(['manual', 'rollback', 'create']);
export const ResponsePromptVariableTypeSchema = z.enum(['string', 'number', 'enum']);
/**
 * content 안의 `{{key}}` 변수 정의.
 *  - type 미지정 (legacy row) → 'string' 으로 해석
 *  - 'number': 테스트 시 숫자 input
 *  - 'enum': enumOptions 중 선택 (drop-down)
 *
 * 서버는 변수 검증 없이 그대로 치환 — type 은 UI 렌더링 힌트.
 */
export const ResponsePromptVariableSchema = z.object({
    key: z.string().regex(/^\w+$/, '영문/숫자/언더스코어만 허용'),
    defaultValue: z.string(),
    type: ResponsePromptVariableTypeSchema.optional(),
    /** type='enum' 일 때 사용. 빈 배열 / 미지정이면 string 으로 fallback. */
    enumOptions: z.array(z.string().min(1).max(80)).max(50).optional(),
});
/** pipeline 참조용 식별자 형식 — 영소문자/숫자/점/하이픈, 시작·끝은 영소문자/숫자. */
export const ResponsePromptSlugSchema = z
    .string()
    .min(2)
    .max(120)
    .regex(/^[a-z0-9][a-z0-9.-]*[a-z0-9]$/, 'slug 은 영소문자/숫자/점/하이픈만 가능 (시작·끝은 영소문자/숫자).');
/** 트리 노드 (폴더 또는 파일). 활성 본문/변수/버전은 variant 에 분리. */
export const ResponsePromptNodeSchema = z.object({
    id: z.string().uuid(),
    parentId: z.string().uuid().nullable(),
    type: ResponsePromptTypeSchema,
    /** file 만 필수, folder 는 null. */
    slug: z.string().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    /**
     * 이 프롬프트 호출 시 사용할 기본 LLM 엔드포인트 id. null = 호출자 컨텍스트 (챗 세션 또는 default) 사용.
     * folder 에서는 의미 없음. invokeResponsePrompt 의 endpoint 결정 우선순위 2 단계.
     */
    preferredEndpointId: z.string().uuid().nullable(),
    sortOrder: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * 모델별 본문 변형. 한 file 노드가 N 개의 variant 를 가지며,
 * 호출 측 endpoint.model 과 modelMatch 가 정확 일치하면 그 variant 사용, 아니면 기본 (modelMatch='').
 */
export const ResponsePromptVariantSchema = z.object({
    id: z.string().uuid(),
    promptId: z.string().uuid(),
    /** '' = 기본 variant. 그 외는 정확 일치 모델 식별자. */
    modelMatch: z.string(),
    /** 표시명 override. null 이면 modelMatch 또는 "기본" 으로 자체 표시. */
    label: z.string().nullable(),
    /** variant 기본 temperature (0~2). null = 모델/엔드포인트/테스트 입력 기본. */
    temperature: z.number().min(0).max(2).nullable(),
    currentContent: z.string().nullable(),
    currentVariables: z.array(ResponsePromptVariableSchema),
    currentVersion: z.number().int().nullable(),
    sortOrder: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** Variant 식별 형식 — '' (기본) 또는 정확 일치 모델 식별자. */
export const ResponsePromptModelMatchSchema = z
    .string()
    .max(120)
    .regex(/^([A-Za-z0-9][A-Za-z0-9._:/-]*[A-Za-z0-9])?$/, 'model_match 는 영숫자/점/하이픈/언더스코어/슬래시/콜론만 가능 (시작·끝은 영숫자).');
export const ResponsePromptTreeNodeSchema = z.lazy(() => ResponsePromptNodeSchema.extend({
    children: z.array(ResponsePromptTreeNodeSchema),
}));
export const ResponsePromptTreeSchema = z.object({
    tree: z.array(ResponsePromptTreeNodeSchema),
});
export const ResponsePromptVersionSchema = z.object({
    id: z.string().uuid(),
    promptId: z.string().uuid(),
    variantId: z.string().uuid(),
    version: z.number().int(),
    content: z.string(),
    variables: z.array(ResponsePromptVariableSchema),
    tags: z.array(z.string()),
    changelog: z.string().nullable(),
    source: ResponsePromptVersionSourceSchema,
    createdBy: z.string().uuid().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.string(),
});
/**
 * 파일 노드 + 모든 variant + 모든 version (평면).
 * 폴더 노드는 variants=[], versions=[].
 * 클라이언트는 selectedVariantId 로 variant 선택 → 그 variant 의 currentContent / versions 만 표시.
 */
export const ResponsePromptDetailSchema = z.object({
    node: ResponsePromptNodeSchema,
    variants: z.array(ResponsePromptVariantSchema),
    versions: z.array(ResponsePromptVersionSchema),
});
export const CreateResponsePromptRequestSchema = z
    .object({
    parentId: z.string().uuid().nullable(),
    type: ResponsePromptTypeSchema,
    /** file 만 필수. 클라이언트가 안 보내고 folder 이면 null 로 저장. */
    slug: ResponsePromptSlugSchema.optional(),
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
})
    .refine((v) => v.type === 'folder' || (v.slug && v.slug.length > 0), {
    message: '파일은 slug 가 필수입니다.',
    path: ['slug'],
});
export const RenameResponsePromptRequestSchema = z.object({
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    /** file 의 slug 변경 (folder 에 보내면 무시). null/미전송 → 변경 안 함. */
    slug: ResponsePromptSlugSchema.optional(),
});
export const MoveResponsePromptRequestSchema = z.object({
    parentId: z.string().uuid().nullable(),
    sortOrder: z.number().int(),
});
/** file 노드만 복제 가능. 원본의 활성 본문/변수를 새 file 의 v1 (source='create') 으로 시드. */
export const DuplicateResponsePromptRequestSchema = z.object({
    name: z.string().min(1).max(120),
    slug: ResponsePromptSlugSchema,
});
export const SaveResponsePromptVersionRequestSchema = z.object({
    /** 어느 variant 의 새 버전인지 명시. */
    variantId: z.string().uuid(),
    content: z.string().max(32000),
    variables: z.array(ResponsePromptVariableSchema),
    tags: z.array(z.string().min(1).max(40)).max(20).default([]),
    changelog: z.string().max(500).optional(),
    /** true 면 저장과 동시에 그 variant 의 활성 버전으로 승격. */
    activate: z.boolean().default(true),
});
// ─── Variant CRUD ────────────────────────────────────────────────────────
/** file 노드에 새 variant 추가. modelMatch 가 '' 또는 정확 일치 식별자. */
export const CreateResponsePromptVariantRequestSchema = z.object({
    modelMatch: ResponsePromptModelMatchSchema,
    label: z.string().min(1).max(80).optional(),
    temperature: z.number().min(0).max(2).optional(),
});
/** Variant 의 label / temperature 만 수정. modelMatch 는 변경 불가 (versions 가 그 키로 묶여 있음). */
export const RenameResponsePromptVariantRequestSchema = z.object({
    label: z.string().max(80).nullable().optional(),
    /** undefined = 변경 안 함, null = 기본값으로 리셋. */
    temperature: z.number().min(0).max(2).nullable().optional(),
});
export const UpdateResponsePromptVersionTagsRequestSchema = z.object({
    tags: z.array(z.string().min(1).max(40)).max(20),
});
/**
 * 여러 파일의 "현재 활성 버전" 에 태그를 일괄 추가/제거.
 * 효과: 각 파일의 active version_id 를 찾아 tags = (현재 ∪ add) \ remove.
 * 활성 버전이 없는 파일은 건너뜀.
 */
export const BulkUpdateActiveTagsRequestSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(200),
    add: z.array(z.string().min(1).max(40)).max(10).default([]),
    remove: z.array(z.string().min(1).max(40)).max(10).default([]),
});
export const BulkUpdateActiveTagsResponseSchema = z.object({
    updated: z.number().int(),
    skipped: z.number().int(),
});
/**
 * Export/Import JSON 포맷.
 *  - v1: 한 file 당 활성 본문/변수 1개 (default variant 만). variant 도입 이전 포맷.
 *  - v2: 한 file 당 N 개 variant 의 활성 본문/변수/temperature/label 까지 round-trip.
 * v1 export 는 v2 import 도 받을 수 있게 — v2 export 도 default variant 의 본문을
 * 최상위 `content/variables` 에 backward-compat 으로 같이 채움.
 * 버전 히스토리는 제외 (백업/이식 목적이라 무거움 회피).
 */
/** Export 안의 variant 항목. v2 에서만 채워짐. */
export const ResponsePromptExportVariantSchema = z.object({
    /** '' = default variant. */
    modelMatch: z.string(),
    label: z.string().nullable(),
    temperature: z.number().min(0).max(2).nullable(),
    content: z.string().nullable(),
    variables: z.array(ResponsePromptVariableSchema),
});
export const ResponsePromptExportItemSchema = z.object({
    tempId: z.string().min(1),
    parentTempId: z.string().nullable(),
    type: ResponsePromptTypeSchema,
    slug: z.string().nullable(),
    name: z.string(),
    description: z.string().nullable(),
    /** v1 backward-compat — default variant 의 활성 본문. v2 import 는 variants 우선. */
    content: z.string().nullable(),
    variables: z.array(ResponsePromptVariableSchema),
    tags: z.array(z.string()),
    /** v2 에서만 채워짐. 비어있으면 v1 호환 — content/variables 만 사용. */
    variants: z.array(ResponsePromptExportVariantSchema).optional(),
});
export const ResponsePromptExportSchema = z.object({
    /** 1 = legacy (default variant 만), 2 = variants 배열 포함. import 는 둘 다 처리. */
    version: z.union([z.literal(1), z.literal(2)]),
    exportedAt: z.string(),
    items: z.array(ResponsePromptExportItemSchema),
});
/**
 * 일부 노드만 export — 선택한 ids 만 (folder 면 자식 자동 포함).
 * 선택에 포함되지 않은 조상 folder 는 export 에 포함되지 않으며 (평탄화),
 * 그런 노드의 parentTempId 는 null 로 떨어진다 → import 시 root 로 들어감.
 */
export const ResponsePromptExportSelectionRequestSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(500),
});
export const ResponsePromptImportResponseSchema = z.object({
    /** 새로 생성된 노드 수 (신규 folder/file). */
    imported: z.number().int(),
    /**
     * 기존 노드와 병합된 수:
     *  - folder 는 같은 parent 안 동일 이름 매치 시 그 folder 재사용 (children 이 그 안으로 import)
     *  - file 은 slug 일치 시 기존 file 에 새 version 추가 + 새 variant 가 있으면 같이 생성
     */
    merged: z.number().int(),
    skipped: z.number().int(),
    errors: z.array(z.object({
        tempId: z.string(),
        /** 사용자가 식별할 수 있도록 원본 item 의 메타 포함 (tempId 는 uuid 라 불편). */
        name: z.string(),
        slug: z.string().nullable(),
        type: ResponsePromptTypeSchema,
        reason: z.string(),
    })),
});
// ─── 테스트 케이스 (회귀) ────────────────────────────────────────────────
export const ResponsePromptTestCaseSchema = z.object({
    id: z.string().uuid(),
    promptId: z.string().uuid(),
    name: z.string(),
    variableValues: z.record(z.string(), z.string()),
    expectedContains: z.string().nullable(),
    note: z.string().nullable(),
    createdBy: z.string().uuid().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const ResponsePromptTestCaseListSchema = z.object({
    cases: z.array(ResponsePromptTestCaseSchema),
});
export const CreateResponsePromptTestCaseRequestSchema = z.object({
    name: z.string().min(1).max(120),
    variableValues: z.record(z.string(), z.string()).default({}),
    expectedContains: z.string().max(500).optional(),
    note: z.string().max(500).optional(),
});
export const RunResponsePromptTestRequestSchema = z.object({
    content: z.string().max(32000),
    variableValues: z.record(z.string(), z.string()).default({}),
    endpointId: z.string().uuid().optional(),
    /**
     * 샘플링 온도. 미전송 / undefined 면 모델·서버 기본값 사용.
     * 범위는 공급자마다 다름 (OpenAI 0~2, Anthropic 0~1, Gemini 0~2) — 어댑터/모델이 거부할 수 있음.
     */
    temperature: z.number().min(0).max(2).optional(),
    /**
     * 어느 variant 의 본문을 실행하는지 (선택). 두 가지 효과:
     *  - audit 로그에 기록 (어떤 variant 가 테스트됐는지 추적)
     *  - temperature 미전송 시 그 variant 의 기본 temperature 로 fallback
     * 매칭 검증은 안 함 — 클라이언트가 결정한 variant 식별자를 그대로 사용.
     */
    variantId: z.string().uuid().optional(),
});
export const RunResponsePromptTestResponseSchema = z.object({
    output: z.string(),
    durationMs: z.number(),
    endpointName: z.string().optional(),
    model: z.string().optional(),
});
// ─── preferred endpoint ─────────────────────────────────────────────────
/** 프롬프트 단위 기본 endpoint 매핑 변경. null = 미설정 (호출자 컨텍스트 사용). */
export const SetResponsePromptPreferredEndpointRequestSchema = z.object({
    endpointId: z.string().uuid().nullable(),
});
// ─── prompt.run 툴 (skill 에서 호출) ────────────────────────────────────
/** LLM 이 prompt.run 툴 호출 시 보내는 인자. */
export const PromptRunToolInputSchema = z.object({
    /** 박물관에 등록된 active file 의 slug. config.allowedPromptSlugs 안에 있어야 함. */
    slug: z.string().min(1),
    /** 본문의 {{key}} 치환에 사용. 미전송 시 빈 객체로 처리. */
    values: z.record(z.string(), z.string()).optional(),
    /** sub-LLM 의 temperature override. variant.temperature 와 endpoint default 보다 우선. */
    temperature: z.number().min(0).max(2).optional(),
});
/** skill_tools.config 의 prompt.run 설정. 빈 배열 금지 — 빈 배열이면 호출 무의미. */
export const PromptRunToolConfigSchema = z.object({
    allowedPromptSlugs: z.array(z.string().min(1)).min(1),
});
// ─── 자동 컨텍스트 주입용 — chat handler 가 loadActiveBySlugs 결과를 가공해 system 메시지 빌드 ──
/** 한 slug 의 활성 정보 (사용 가능한 프롬프트 안내문 빌드에 사용). */
export const ActiveResponsePromptSummarySchema = z.object({
    slug: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    /** default variant 의 currentVariables 의 key 목록 (안내문에 변수 키 표시). */
    variableKeys: z.array(z.string()),
    /** 보유 variant 의 modelMatch 배열 ('' 은 default). UI/안내문에 표시. */
    variantModelMatches: z.array(z.string()),
    /** prompt 단위 기본 endpoint 가 설정돼 있는지 (안내문에 "고정 endpoint" 표시용). */
    hasPreferredEndpoint: z.boolean(),
});
//# sourceMappingURL=response-prompts.js.map